import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Sheet } from "@/components/ui/Sheet";
import { ApiError, apiFetch } from "@/lib/api";
import type { EquipmentReplacementResource, EquipmentResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * "Replace" (Story 12.5, AC, spec judgement call 4) — a real form, not a
 * bare confirm: the AC's own "fill in the new item's own real Identity/
 * Purchase/Warranty fields" before confirming. Mirrors `AddEquipmentDialog.
 * tsx`'s own phone-first Dialog/Sheet shape closely, deliberately narrowed
 * to exactly the fields the Owner enters fresh for the new physical item —
 * `property_id`/`unit_id`/`category`/`specific_location`/`fuel_type`/
 * `capacity` are never asked here at all (this story's own Intent:
 * installation-context facts that always carry over from the old row,
 * resolved entirely server-side, `app/api/equipment.py::
 * ReplaceEquipmentRequest`'s own module doc comment).
 *
 * `expected_useful_life_years` still pre-fills from the category default —
 * the identical `GET /equipment/useful-life-defaults` mechanism `Add
 * EquipmentDialog.tsx` already uses, keyed off the OLD row's own category
 * (which the new row will also carry, since the category itself never
 * changes on a replace) — never copied from the old row's own possibly-
 * stale value.
 */

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export function ReplaceEquipmentDialog({
  open,
  onOpenChange,
  oldEquipment,
  onReplaced,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  oldEquipment: EquipmentResource;
  onReplaced: (result: EquipmentReplacementResource) => void;
}) {
  const [make, setMake] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [serialNumber, setSerialNumber] = useState("");

  const [moreOpen, setMoreOpen] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [retailerOrInstaller, setRetailerOrInstaller] = useState("");
  const [warrantyType, setWarrantyType] = useState("");
  const [warrantyLength, setWarrantyLength] = useState("");
  const [warrantyExpirationDate, setWarrantyExpirationDate] = useState("");
  const [hasExtendedWarranty, setHasExtendedWarranty] = useState("");
  const [claimPhoneNumber, setClaimPhoneNumber] = useState("");
  const [usefulLifeYears, setUsefulLifeYears] = useState("");
  const [usefulLifeTouched, setUsefulLifeTouched] = useState(false);
  const [replacementCostEstimate, setReplacementCostEstimate] = useState("");
  const [condition, setCondition] = useState("");
  const [serviceInterval, setServiceInterval] = useState("");
  const [serviceIntervalMonths, setServiceIntervalMonths] = useState("");
  const [lastServiceDate, setLastServiceDate] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  const usefulLifeDefaultsQuery = useQuery({
    queryKey: ["equipment", "useful-life-defaults"],
    queryFn: () => apiFetch<Record<string, number>>("/equipment/useful-life-defaults"),
    enabled: open,
  });

  useEffect(() => {
    if (open && !usefulLifeTouched) {
      const defaultYears = usefulLifeDefaultsQuery.data?.[oldEquipment.category];
      setUsefulLifeYears(defaultYears === undefined ? "" : String(defaultYears));
    }
    // Only re-runs when the defaults query resolves or the dialog opens —
    // never once the Owner has typed their own value (VC-12.1-04's own
    // "never fires at all once touched" discipline, reused here).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usefulLifeDefaultsQuery.data]);

  useEffect(() => {
    if (!open) {
      setMake("");
      setModelNumber("");
      setSerialNumber("");
      setMoreOpen(false);
      setPurchaseDate("");
      setPurchasePrice("");
      setRetailerOrInstaller("");
      setWarrantyType("");
      setWarrantyLength("");
      setWarrantyExpirationDate("");
      setHasExtendedWarranty("");
      setClaimPhoneNumber("");
      setUsefulLifeYears("");
      setUsefulLifeTouched(false);
      setReplacementCostEstimate("");
      setCondition("");
      setServiceInterval("");
      setServiceIntervalMonths("");
      setLastServiceDate("");
      setError(undefined);
    }
  }, [open]);

  function handleUsefulLifeChange(value: string) {
    setUsefulLifeTouched(true);
    setUsefulLifeYears(value);
  }

  const canSubmit = !isSubmitting;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const result = await apiFetch<EquipmentReplacementResource>(`/equipment/${oldEquipment.id}/replace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          make: make.trim() === "" ? null : make.trim(),
          model_number: modelNumber.trim() === "" ? null : modelNumber.trim(),
          serial_number: serialNumber.trim() === "" ? null : serialNumber.trim(),
          purchase_date: purchaseDate === "" ? null : purchaseDate,
          purchase_price_cents: purchasePrice === "" ? null : Math.round(Number(purchasePrice) * 100),
          retailer_or_installer: retailerOrInstaller.trim() === "" ? null : retailerOrInstaller.trim(),
          warranty_type: warrantyType.trim() === "" ? null : warrantyType.trim(),
          warranty_length: warrantyLength.trim() === "" ? null : warrantyLength.trim(),
          warranty_expiration_date: warrantyExpirationDate === "" ? null : warrantyExpirationDate,
          has_extended_warranty: hasExtendedWarranty === "" ? null : hasExtendedWarranty === "true",
          claim_phone_number: claimPhoneNumber.trim() === "" ? null : claimPhoneNumber.trim(),
          expected_useful_life_years: usefulLifeYears === "" ? null : Number(usefulLifeYears),
          replacement_cost_estimate_cents:
            replacementCostEstimate === "" ? null : Math.round(Number(replacementCostEstimate) * 100),
          condition: condition.trim() === "" ? null : condition.trim(),
          service_interval: serviceInterval.trim() === "" ? null : serviceInterval.trim(),
          service_interval_months: serviceIntervalMonths === "" ? null : Number(serviceIntervalMonths),
          last_service_date: lastServiceDate === "" ? null : lastServiceDate,
        }),
      });

      onOpenChange(false);
      onReplaced(result);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Container
      open={open}
      onOpenChange={onOpenChange}
      title="Replace equipment"
      description="The new item's own installation details (property, unit, category, location) carry over automatically. Enter the new item's own identity, purchase, and warranty details below."
    >
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("make")}</span>
          <Input autoFocus value={make} onChange={(event) => setMake(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("model_number")}</span>
          <Input value={modelNumber} onChange={(event) => setModelNumber(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("serial_number")}</span>
          <Input value={serialNumber} onChange={(event) => setSerialNumber(event.target.value)} />
        </label>

        <button
          type="button"
          onClick={() => setMoreOpen((value) => !value)}
          aria-expanded={moreOpen}
          className="inline-flex h-tap-target-min items-center self-start text-body font-medium text-accent underline"
        >
          {moreOpen ? "Hide details" : "More details"}
        </button>

        {moreOpen ? (
          <div className="flex flex-col gap-4 border-t border-border-hairline pt-4">
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("purchase_date")}</span>
              <Input type="date" value={purchaseDate} onChange={(event) => setPurchaseDate(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("purchase_price_cents")}</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={purchasePrice}
                onChange={(event) => setPurchasePrice(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("retailer_or_installer")}</span>
              <Input value={retailerOrInstaller} onChange={(event) => setRetailerOrInstaller(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("warranty_type")}</span>
              <Input
                value={warrantyType}
                onChange={(event) => setWarrantyType(event.target.value)}
                placeholder="e.g. parts, labor, full"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("warranty_length")}</span>
              <Input value={warrantyLength} onChange={(event) => setWarrantyLength(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("warranty_expiration_date")}</span>
              <Input
                type="date"
                value={warrantyExpirationDate}
                onChange={(event) => setWarrantyExpirationDate(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("has_extended_warranty")}</span>
              <Select value={hasExtendedWarranty} onChange={(event) => setHasExtendedWarranty(event.target.value)}>
                <option value="">Unspecified</option>
                {YES_NO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("claim_phone_number")}</span>
              <Input value={claimPhoneNumber} onChange={(event) => setClaimPhoneNumber(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">
                {labelFor("expected_useful_life_years")}
              </span>
              <Input
                type="number"
                min="0"
                value={usefulLifeYears}
                onChange={(event) => handleUsefulLifeChange(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">
                {labelFor("replacement_cost_estimate_cents")}
              </span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={replacementCostEstimate}
                onChange={(event) => setReplacementCostEstimate(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("condition")}</span>
              <Input value={condition} onChange={(event) => setCondition(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("service_interval")}</span>
              <Input
                value={serviceInterval}
                onChange={(event) => setServiceInterval(event.target.value)}
                placeholder="e.g. annual"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("service_interval_months")}</span>
              <Input
                type="number"
                min="1"
                value={serviceIntervalMonths}
                onChange={(event) => setServiceIntervalMonths(event.target.value)}
                placeholder="e.g. 12"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("last_service_date")}</span>
              <Input type="date" value={lastServiceDate} onChange={(event) => setLastServiceDate(event.target.value)} />
            </label>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting ? "Replacing…" : "Replace equipment"}
          </Button>
        </div>
      </form>
    </Container>
  );
}
